<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { PDFtoIMG } from "@/common/services/convert";

import { fileCreate, fileDelete } from "@/common/api/rest/file/file";
import {
  certificateList,
  certificateCreate,
  certificatePatch,
  certificateDelete,
} from "@/common/api/rest/certificate";

import type { FileType } from "@/common/types/common";
import type { CertificateType } from "@/common/types/certificate";

import { fileUploadForm, viewImage } from "@/components";
import {
  baseConfirm,
  baseInput,
  baseDate,
  baseSelect,
  baseTextarea,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    accountId: string;
    equipmentId: string;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);
const fileExtensions = [".bmp", ".jpg", ".jpeg", ".pdf"];

const certificates = ref<CertificateType[]>([]);
const selected = ref<CertificateType | null>(null);
const selectedImageId = ref<string | null>(null);
const showImage = ref<string | null>(null);

const showConfirmDel = ref(false);
const showConfirmImageDel = ref(false);

const fileKey = ref(1);

const statusOptions = computed(() => [
  {
    option: t("common.labels.active"),
    value: "ACTIVE",
  },
  {
    option: t("common.labels.inactive"),
    value: "INACTIVE",
  },
]);

function fecthCertificates() {
  if (!props.equipmentId) return;

  certificateList(props.accountId, props.equipmentId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        certificates.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update(index: number, key: keyof CertificateType) {
  const certificate = certificates.value[index];

  certificatePatch(props.accountId, certificate.certificate_id, {
    [key]: certificate[key],
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthCertificates();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addCertificate() {
  if (!props.equipmentId) return;

  certificateCreate(props.accountId, props.equipmentId)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthCertificates();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function delCertificate(index: number) {
  selected.value = certificates.value[index];
  showConfirmDel.value = true;
}

function delCertificateCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function delCertificateConfirm() {
  if (!selected.value) return;

  showConfirmDel.value = false;
  certificateDelete(props.accountId, selected.value.certificate_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fecthCertificates();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function uploadFile(index: number, fileData: FileType) {
  const { extension, name, file, infos } = fileData;

  const certificate = certificates.value[index];

  if (extension?.toLowerCase() === "pdf") {
    const converted = await PDFtoIMG(file);

    if (!converted.success) {
      store.commit("SET_LOADING", false);
      toast({ message: t("error.default"), type: "error" });
      return;
    }

    if (!converted.pages?.length) {
      store.commit("SET_LOADING", false);
      fileKey.value++;
      return;
    }

    const promises = converted.pages.map((item) =>
      fileCreate(props.accountId, certificate.certificate_id, {
        file: item.image.split(",")[1],
        type: "CERTIFICATE",
        name,
        extension: "jpg",
        page: item.page,
        infos: item.image.split(",")[0],
      }),
    );

    await Promise.all(promises).finally(() => {
      store.commit("SET_LOADING", false);
      fileKey.value++;
      fecthCertificates();
    });
  } else if (extension?.toLowerCase() === "jpg") {
    await fileCreate(props.accountId, certificate.certificate_id, {
      file: file,
      type: "CERTIFICATE",
      name,
      extension: "jpg",
      page: 0,
      infos: infos,
    }).finally(() => {
      store.commit("SET_LOADING", false);
      fileKey.value++;
      fecthCertificates();
    });
  } else {
    toast({ message: t("error.file_not_supported"), type: "error" });
    store.commit("SET_LOADING", false);
    fileKey.value++;
  }
}

function removeImage(index: number, file_id?: string) {
  if (!file_id) return;

  const certificate = certificates.value[index];

  selected.value = certificate;
  selectedImageId.value = file_id;
  showConfirmImageDel.value = true;
}

function removeImageCancel() {
  selected.value = null;
  selectedImageId.value = null;
  showConfirmImageDel.value = false;
}

function removeImageConfirm() {
  if (!selected.value || !selectedImageId.value) return;

  showConfirmImageDel.value = false;
  store.commit("SET_LOADING", true);

  fileDelete(props.accountId, selectedImageId.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        removeImageCancel();
        fecthCertificates();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

const debounceUpdate = debounce(update, 1000);

onMounted(() => {
  fecthCertificates();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <template v-if="certificates.length === 0">
        <div class="box-empty">
          {{ t("equipment.texts.equipment_empty") }}
          <button class="btn btn-md btn-primary" @click="addCertificate()">
            {{
              t("common.labels.add", {
                concat: t("common.labels.certificate"),
              }).toUpperCase()
            }}
          </button>
        </div>
      </template>

      <template v-else>
        <div class="certificates">
          <template
            v-for="(item, index) in certificates"
            :key="item.certificate_id"
          >
            <div v-if="item" class="item">
              <div class="item-content">
                <div class="form">
                  <base-select
                    class="flex-2"
                    v-model="item.status"
                    :value="item.status"
                    :options="statusOptions"
                    :is-empty="false"
                    @change="update(index, 'status')"
                  >
                    <template #label>
                      {{ t("common.labels.status") }} *
                    </template>
                  </base-select>

                  <base-input
                    class="flex-3"
                    v-model="item.name"
                    :value="item.name"
                    required
                    @input="debounceUpdate(index, 'name')"
                  >
                    <template #label> {{ t("common.labels.name") }} *</template>
                  </base-input>

                  <div class="flex flex-row gap-4">
                    <base-date
                      v-model="item.start_date"
                      :value="item.start_date"
                      :required="true"
                      value-mask="YYYY-MM-DD"
                      placeholder="--/--/----"
                      @update:model-value="debounceUpdate(index, 'start_date')"
                    >
                      <template #label>
                        {{ t("common.labels.date") }} *
                      </template>
                    </base-date>

                    <base-date
                      v-model="item.end_date"
                      :value="item.end_date"
                      :required="true"
                      value-mask="YYYY-MM-DD"
                      placeholder="--/--/----"
                      @update:model-value="debounceUpdate(index, 'end_date')"
                    >
                      <template #label>
                        {{ t("common.labels.validity") }} *
                      </template>
                    </base-date>
                  </div>

                  <base-textarea
                    v-model="item.description"
                    :value="item.description"
                    :placeholder="t('common.placeholders.describe')"
                    @input="debounceUpdate(index, 'description')"
                  >
                    <template #label>
                      {{ t("common.labels.description") }}
                    </template>
                  </base-textarea>
                </div>

                <div
                  class="certificate"
                  :class="{
                    'has-images': item.files,
                  }"
                >
                  <templete v-if="item.files">
                    <div class="images">
                      <template
                        v-for="(image, img_idx) in item.files"
                        :key="img_idx"
                      >
                        <div class="image">
                          <img
                            :src="image.file"
                            :alt="`image ${img_idx + 1}`"
                            @click="showImage = image.file"
                          />
                          <div class="actions">
                            <button
                              class="btn btn-sm btn-default"
                              @click="removeImage(index, image.file_id)"
                            >
                              <svg-icon icon="trash" size="1.25rem" />
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>
                  </templete>

                  <div class="upload">
                    <file-upload-form
                      :key="fileKey"
                      :extensions="fileExtensions"
                      @update-data="uploadFile(index, $event)"
                    />
                  </div>
                </div>
              </div>

              <div class="flex flex-row justify-end w-full">
                <button
                  class="btn btn-md btn-danger"
                  @click="delCertificate(index)"
                >
                  <svg-icon icon="trash" size="1.5rem" />
                  {{
                    t("common.labels.delete", {
                      concat: t("common.labels.certificate"),
                    })
                  }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="box-add">
          <button class="btn btn-md btn-primary" @click="addCertificate()">
            {{
              t("common.labels.add", {
                concat: t("common.labels.certificate"),
              }).toUpperCase()
            }}
          </button>
        </div>
      </template>
    </div>
  </section>

  <view-image :image="showImage" @close="showImage = null" />

  <base-confirm
    max-width="400px"
    :show="showConfirmDel"
    :title="
      t('common.labels.remove', {
        concat: t('common.labels.certificate'),
      }).toUpperCase()
    "
    :message="
      t('common.texts.remove_message', {
        concat: t('common.labels.certificate').toUpperCase(),
      })
    "
    @cancel="delCertificateCancel"
    @confirm="delCertificateConfirm"
  />

  <base-confirm
    max-width="400px"
    :show="showConfirmImageDel"
    :title="t('common.modals.titles.remove_image').toUpperCase()"
    :message="t('common.modals.messages.remove_image')"
    @cancel="removeImageCancel"
    @confirm="removeImageConfirm"
  />
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-block: 1.5rem;
  padding-inline: 1.5rem;

  .certificates {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    .item {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding-block-end: 2rem;
      border-block-end: 1px solid var(--border);

      &-content {
        display: flex;
        flex-direction: row;
        gap: 2rem;
      }

      .certificate {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        background: var(--white-off);
        border-radius: 0.5rem;

        &.has-images {
          padding-block: 2rem;
          justify-content: flex-start;
        }

        .images {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          justify-content: space-between;
          width: 80%;
          margin-inline: auto;
          gap: 5%;
          margin-block-end: 2rem;

          .image {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;

            img {
              cursor: pointer;
              width: 100%;
            }
          }
        }

        .upload {
          width: 80%;
          max-width: 80%;
          margin-inline: auto;
          background: var(--white);
        }
      }

      .form {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
    }
  }

  .documents {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .box-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }

  .box-add {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block-start: 3rem;
    padding-block-end: 1rem;
  }
}

@media screen and (max-width: 1024px) {
  .tab-component {
    .certificates {
      .item {
        &-content {
          flex-direction: column;
        }
      }
    }
  }
}
</style>
